import React from "react";
import Layout from "@/components/Layout";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  MapPin,
  Briefcase,
  Heart,
  Zap,
  TrendingUp,
  Mail,
  CheckCircle2,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import openings from "./openings.json";

interface JobOpening {
  jobid: string;
  title: string;
  jobtype: string;
  location: string;
  description: string;
  qualification: string;
  requirements: string[];
  apply_link: string;
}

const cultureCards = [
  {
    icon: Heart,
    title: "Our Culture",
    description:
      "We value curiosity, ownership, and collaboration. We ship thoughtfully, learn continuously, and celebrate meaningful outcomes for our users.",
    accent: "text-primary",
    bg: "bg-primary/10",
    bar: "from-primary to-primary/40",
  },
  {
    icon: Zap,
    title: "How We Work",
    description:
      "Pragmatic, product-focused, and patient-first. We prefer clear goals, simple solutions, strong craftsmanship, and reliable delivery.",
    accent: "text-secondary",
    bg: "bg-secondary/10",
    bar: "from-secondary to-secondary/40",
  },
  {
    icon: TrendingUp,
    title: "Benefits & Growth",
    description:
      "Competitive benefits, flexible work arrangements, and opportunities to grow your skills across product, engineering, and operations.",
    accent: "text-accent",
    bg: "bg-accent/10",
    bar: "from-accent to-accent/40",
  },
];

const HumanResource: React.FC = () => {
  const [jobs, setJobs] = React.useState<JobOpening[]>([]);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const pageSize = 1;

  React.useEffect(() => {
    setJobs(openings as JobOpening[]);
  }, []);

  const totalPages = Math.max(1, Math.ceil(jobs.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const pagedJobs = jobs.slice(startIndex, startIndex + pageSize);

  const goToPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <Layout>
      {/* ─── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative py-28 overflow-hidden bg-gradient-to-br from-primary/8 via-background to-secondary/8">
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/4 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3 pointer-events-none" />

        <div className="container mx-auto px-4 text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-semibold mb-8">
            <Sparkles className="h-3.5 w-3.5" />
            Careers at Wellnext
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-[1.1]">
            <span className="text-gradient-primary">Join our team</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            We're building future-ready health solutions. If you're passionate about impact,
            innovation, and patient-centric outcomes, you'll feel right at home.
          </p>
        </div>
      </section>

      {/* ─── Current Openings ─────────────────────────────────────────────── */}
      <section className="py-24 bg-background" aria-live="polite">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-14">
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
              Open Roles
            </span>
            <h2 className="text-4xl font-bold mb-4">
              <span className="text-gradient-primary">Current Openings</span>
            </h2>
          </div>

          {jobs.length === 0 ? (
            <div className="rounded-3xl border border-border/60 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-12 text-center">
              <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
                <Briefcase className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">Openings coming soon</h3>
              <p className="text-muted-foreground">
                Current openings will be listed here shortly. Send your CV in the meantime.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              {pagedJobs.map((job) => (
                <article
                  key={job.jobid}
                  className="rounded-3xl border border-border/60 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_10px_36px_rgba(0,0,0,0.10)] transition-all duration-300 overflow-hidden"
                >
                  <div className="h-[3px] w-full bg-gradient-to-r from-primary via-secondary to-accent" />
                  <div className="p-8">
                    <header className="mb-5">
                      <h3 className="text-2xl font-bold text-foreground mb-3">{job.title}</h3>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                          <MapPin className="h-3 w-3" />
                          {job.location}
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-semibold">
                          <Briefcase className="h-3 w-3" />
                          {job.jobtype}
                        </span>
                      </div>
                    </header>

                    <p className="text-muted-foreground leading-relaxed mb-5">{job.description}</p>

                    {job.qualification && (
                      <div className="mb-5 p-4 rounded-2xl bg-muted/40 border border-border/40">
                        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">
                          Qualification
                        </p>
                        <p className="text-sm text-foreground leading-relaxed">{job.qualification}</p>
                      </div>
                    )}

                    {job.requirements?.length ? (
                      <ul className="space-y-2 mb-7">
                        {job.requirements.slice(0, 4).map((req, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-sm text-muted-foreground">
                            <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}

                    <div className="flex items-center justify-between pt-5 border-t border-border/40">
                      <a
                        href={`/apply-job?jobid=${job.jobid}`}
                        className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
                        aria-label={`Apply for ${job.title}`}
                      >
                        Apply Now
                        <ArrowRight className="h-4 w-4" />
                      </a>
                      <span className="text-xs text-muted-foreground">Job ID: {job.jobid}</span>
                    </div>
                  </div>
                </article>
              ))}

              <Pagination className="pt-2" aria-label="Job pagination">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(event) => { event.preventDefault(); goToPage(currentPage - 1); }}
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : undefined}
                      aria-disabled={currentPage === 1}
                      tabIndex={currentPage === 1 ? -1 : undefined}
                    />
                  </PaginationItem>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                    <PaginationItem key={pageNumber}>
                      <PaginationLink
                        href="#"
                        isActive={pageNumber === currentPage}
                        onClick={(event) => { event.preventDefault(); goToPage(pageNumber); }}
                        aria-label={`Go to page ${pageNumber}`}
                      >
                        {pageNumber}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(event) => { event.preventDefault(); goToPage(currentPage + 1); }}
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : undefined}
                      aria-disabled={currentPage === totalPages}
                      tabIndex={currentPage === totalPages ? -1 : undefined}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      </section>

      {/* ─── Culture Cards ────────────────────────────────────────────────── */}
      <section className="py-24 bg-gradient-to-b from-muted/40 to-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-primary mb-3 block">
              Life at Wellnext
            </span>
            <h2 className="text-4xl font-bold">
              <span className="text-gradient-primary">What it's like to work here</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {cultureCards.map(({ icon: Icon, title, description, accent, bg, bar }) => (
              <div
                key={title}
                className="group rounded-3xl border border-border/60 bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] hover:-translate-y-1 transition-all duration-500 overflow-hidden"
              >
                <div className={`h-[3px] w-full bg-gradient-to-r ${bar}`} />
                <div className="p-8">
                  <div className={`h-12 w-12 rounded-2xl ${bg} flex items-center justify-center mb-5`}>
                    <Icon className={`h-6 w-6 ${accent}`} />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">{title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Send CV CTA ──────────────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-secondary/5 to-background relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
        <div className="absolute right-0 top-0 w-80 h-80 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 relative text-center">
          <div className="max-w-xl mx-auto">
            <div className="inline-flex h-14 w-14 rounded-2xl bg-primary/10 items-center justify-center mx-auto mb-6">
              <Mail className="h-7 w-7 text-primary" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Don't see the right role?
            </h2>
            <p className="text-muted-foreground mb-2 text-lg">
              If you believe you can contribute, we'd love to hear from you.
            </p>
            <p className="text-muted-foreground mb-8">
              Send your CV to{" "}
              <a
                href="mailto:hr@wellnextek.com"
                className="text-primary font-semibold hover:underline"
              >
                hr@wellnextek.com
              </a>
            </p>
            <a
              href="mailto:hr@wellnextek.com"
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-7 py-3 text-base font-semibold text-primary-foreground hover:opacity-90 transition-opacity shadow-[0_4px_14px_rgba(0,0,0,0.12)]"
            >
              Send Your CV
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HumanResource;
